import {Injectable} from '@angular/core';
import {
  Check,
  Event,
  EventLimited,
  EventPage,
  EventsService,
  EventStateEnum,
  EventStatus,
  EventType,
  EventVisibilityType,
  EventWithLegal,
  PriceClass,
  PriceType,
  Right,
  SeatInfo,
  Sector
} from '../../generated/sales-terminal-client';
import {LanguageService} from '../language/language.service';
import {AuthService} from '../auth/auth.service';
import {Observable, tap, throwError} from 'rxjs';
import {SnackService} from '../snack/snack.service';
import {TICKETS_SHOW_EXACT_NUMBER_LIMIT} from '../../modules/sales-terminal/sales-terminal.constants';
import {PageableResult, PageableSearch, ScrollLoadService, SortableSearch} from '../../components/scroll-load-component/scroll-load.component';
import {PeriodPipe} from '../../pipes/period/period.pipe';
import {ScrollLoad} from '../../components/scroll-load-component/ScrollLoad';
import {TranslateService} from '@ngx-translate/core';
import {Privileges} from '../../modules/admin/privileges/privileges.constants';
import {
  STATUS_CANCELLED,
  STATUS_DRAFT,
  STATUS_PAST,
  STATUS_REFUND,
  STATUS_SUSPENDED,
  SUBSTATUS_PRE_SALE_ENDED,
  SUBSTATUS_PRE_SALE_WITH_CODE,
  SUBSTATUS_SALE_STARTS_ON,
  SUBSTATUS_SOLD_OUT,
  SUBSTATUS_TICKETS_UNAVAILABLE
} from '../../modules/sales-terminal/event-search/event-search.constants';
import {CartDraftEvent} from '../cart/cart-base.service';
import {S3UploaderRatioEnum, S3UploaderRatios} from '../../components/s3/uploader/s3-uploader.types';
import moment from 'moment-timezone';
import {RecentlyFinishedFilter} from '../../modules/back-office/recently-finished/recently-finished.component';

export interface EventSearchFilter {
  dateStart?: string;
  dateEnd?: string;
  eventId?: number;
  organisers?: number[];
  status?: number;
  showId?: number;
  eventVenueId?: number;
  legalPersonId?: number;
}
export interface EventSearchData extends SortableSearch {
  q?: string;
  l?: string;
  filters?: EventSearchFilter;
}
export interface LimitedEventSearchData extends PageableSearch {
  q?: string;
  showId?: number;
  eventIds?: number[];
  statusIds?: number[];
  eventStart?: string;
  eventEnd?: string;
  eventVenueId?: number;
  legalPersonId?: number;
  eventType?: EventType;
}

@Injectable()
export class EventService implements ScrollLoadService<EventLimited> {

  constructor(private _api: EventsService,
              private languageService: LanguageService,
              private authService: AuthService,
              private snackService: SnackService,
              private periodPipe: PeriodPipe,
              private translateService: TranslateService) {}

  public search(data: LimitedEventSearchData): Observable<PageableResult<EventLimited>> {
    return this._api.searchLimitedEvents(
      data.q,
      data.showId,
      data.eventIds,
      data.statusIds,
      data.eventStart,
      data.eventEnd,
      data.page,
      data.pageSize,
      data.eventVenueId,
      data.legalPersonId,
      data.eventType
    ).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public displayScrollLoadOption(option: EventLimited): string {
    return '<span class="medium">' + option.title + '</span>&nbsp;<i>' + this.periodPipe.transform(option.eventDate) + '</i>';
  }
  public displayScrollLoadSelection(selection: EventLimited): string | undefined {
    return selection?.title || undefined;
  }
  public getScrollLoad(pageSize?: number, idKey?: string): ScrollLoad<EventLimited> {
    return new ScrollLoad<EventLimited>(pageSize || 10, idKey || 'id', 'q');
  }

  public searchEventsPageable(data: EventSearchData): Observable<EventPage> {
    return this._api.searchEvents(
      data.q,
      data.l || this.languageService.selectedLang,
      data.page,
      data.pageSize,
      data.sortBy as ('title' | 'eventStartUtc'),
      data.sortOrder,
      data.filters?.dateStart || null,
      data.filters?.dateEnd || null,
      data.filters?.eventId ? [data.filters.eventId] : null,
      data.filters?.organisers || null,
      data.filters?.status || null,
      !data.q && !Object.values(data.filters).filter(f => !!f).length && this.authService.user.hasDashboard || null,
      data.filters?.showId || null,
      data.filters?.eventVenueId || null,
      data.filters?.legalPersonId || null
    ).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getEvent(eventId: number): Observable<Event> {
    if (!eventId) {
      return throwError(() => this.snackService.showBackendError(null, 'general.api_event_not_found'));
    } else {
      return this._api.findById(eventId, this.languageService.selectedLang).pipe(
        tap({error: (e) => this.snackService.showBackendError(e)})
      );
    }
  }

  public getEventSectors(eventId: number): Observable<Sector[]> {
    return this._api.getEventSectors(eventId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getEventSeatInfo(eventId: number, sectorId: number): Observable<SeatInfo[]> {
    return this._api.getEventSectorSeatInfo(eventId, sectorId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getEventSectorPriceClass(eventId: number, sectorId: number): Observable<PriceClass[]> {
    return this._api.getEventSectorPriceClass(eventId, sectorId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getEventSectorPriceTypeByClass(eventId: number, sectorId: number, classId: number): Observable<PriceType[]> {
    return this._api.getEventSectorPriceTypeByClass(eventId, sectorId, classId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public findAllEventStatuses():  Observable<EventStatus[]> {
    return this._api.findAllEventStatuses().pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public isSameOrganiser(eventsIds: number[]): Observable<Check> {
    return this._api.isSameOrganiser(eventsIds).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public isOwner(eventsIds: number[]): Observable<Right> {
    return this._api.isowner(eventsIds).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getFinishedRecently(filter: RecentlyFinishedFilter): Observable<PageableResult<EventWithLegal>> {
    return this._api.getEventsFinishedRecently(
      filter?.page,
      filter?.pageSize,
      filter?.name || null,
      filter?.id || null,
      filter?.projectManagerId || null,
      filter?.period?.start || null,
      filter?.period?.end || null
    ).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public getFinishedRecentlyCsv(filter: RecentlyFinishedFilter): Observable<string> {
    return this._api.getEventsFinishedRecentlyCsv(
      this.languageService.selectedLang,
      filter?.name || null,
      filter?.id || null,
      filter?.projectManagerId || null,
      filter?.period?.start || null,
      filter?.period?.end || null,
      'body', false, {httpHeaderAccept: 'text/csv'}
    ).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  // Without privilege progress returns '21' aka 'TICKETS_SHOW_EXACT_NUMBER_LIMIT + 1' in case there are more than 20 tickets available.
  public getFreePlacesString(inFreePlaces: number): string {
    return inFreePlaces > TICKETS_SHOW_EXACT_NUMBER_LIMIT && !this.authService.checkPrivileges(Privileges.TICKETS_SHOW_EXACT_NUMBER) ?
      (TICKETS_SHOW_EXACT_NUMBER_LIMIT + '+') : inFreePlaces.toString();
  }

  public translateEventStatus(status: number): string {
    if (status <= 9) {
      return this.translateService.instant('classifiers.concert_status.name_' + status);
    }
    return this.translateService.instant('general.api_classifiers_concert_substatus_name_' + status);
  }

  // Event search items
  public getEventImage(event: Event, ratio: S3UploaderRatioEnum = S3UploaderRatios.Modern): string {
    return event.image ? (event.boEvent ? '' : this.getImagePath(event.eventId, event.image)) : ('url(\'/assets/img/jpg/event-placeholder-' + ratio + '.jpg\')');
  }
  public getImagePath(eventId: number, image: string): string {
    return 'url(\'https://shop.piletilevi.ee/public/image/type/concert277Image/id/' + eventId + '/filename/' + image + '\')';
  }

  public isPresaleEnded(substatusId: number): boolean {
    return substatusId === SUBSTATUS_PRE_SALE_ENDED;
  }
  public isSoldOut(substatusId: number): boolean {
    return substatusId === SUBSTATUS_SOLD_OUT;
  }
  public isSaleStartsOn(substatusId: number): boolean {
    return substatusId === SUBSTATUS_SALE_STARTS_ON;
  }
  public isSaleStartsFuture(saleStart: string): boolean {
    const now: moment.Moment = moment.utc(new Date());
    const saleStartMoment: moment.Moment = moment.utc(saleStart || null);
    return saleStartMoment.isAfter(now);
  }
  public isSuspended(substatusId: number): boolean {
    return substatusId === STATUS_SUSPENDED;
  }
  public isPresaleWithCode(event: Event): boolean {
    return event.substatusId === SUBSTATUS_PRE_SALE_WITH_CODE || event.assignedStates.includes(EventStateEnum.SaleWithCode);
  }
  public isTicketsUnavailable(substatusId: number): boolean {
    return substatusId === SUBSTATUS_TICKETS_UNAVAILABLE;
  }
  public isTicketsUnavailableState(assignedStates: Array<EventStateEnum>): boolean {
    return assignedStates.includes(EventStateEnum.TicketsCurrentlyUnavailable);
  }
  public isSoldOutState(assignedStates: Array<EventStateEnum>): boolean {
    return assignedStates.includes(EventStateEnum.SoldOut);
  }
  public isPostponedState(assignedStates: Array<EventStateEnum>): boolean {
    return assignedStates.includes(EventStateEnum.Postponed);
  }
  public isRefundableState(assignedStates: Array<EventStateEnum>): boolean {
    return assignedStates.includes(EventStateEnum.Refundable);
  }
  public isPresaleEndedState(assignedStates: Array<EventStateEnum>): boolean {
    return assignedStates.includes(EventStateEnum.PreSaleEnded);
  }

  public isRefundable(statusId: number): boolean {
    return statusId === STATUS_REFUND;
  }
  public isDraft(statusId: number): boolean {
    return statusId === STATUS_DRAFT;
  }

  public canBuy(event: Event): boolean {
      return (!this.isRefundable(event.status.id) && (!event.substatusId || this.isSaleStartsOn(event.substatusId))) && this.hasCanBuyState(event.assignedStates);
  }

  private hasCanBuyState(assignedStates: EventStateEnum[]): boolean {
    return assignedStates.filter(state => state !== EventStateEnum.PreSaleEnded && state !== EventStateEnum.Refundable).length === 0;
  }

  public hasAllowedStatus(statusId: number): boolean {
    let result: boolean = ![STATUS_PAST, STATUS_SUSPENDED, STATUS_CANCELLED].includes(statusId);
    if (this.isDraft(statusId)) {
      result = this.authService.checkPrivileges(Privileges.EVENT_VIEW_PREPARATION);
    }
    return result;
  }
  public hasAllowedSubstatus(event: Event): boolean {
    if (this.isHiddenEvent(event)) {
      return this.canAccessHiddenEvent;
    }
    return [undefined, null, SUBSTATUS_SOLD_OUT, SUBSTATUS_PRE_SALE_WITH_CODE, SUBSTATUS_SALE_STARTS_ON, SUBSTATUS_TICKETS_UNAVAILABLE]
      .includes(event.substatusId) || event.assignedStates.includes(EventStateEnum.SaleWithCode);
  }

  public isHiddenEvent(event: Event): boolean {
    return this.isSaleStartsOn(event.substatusId) || (!!event.publicAvailability && moment.utc(event.publicAvailability).isAfter(moment.utc()));
  }

  public get canAccessHiddenEvent(): boolean {
    // booking/buying tickets before sale start date is only allowed for users with a special privilege
    return this.authService.checkPrivileges(Privileges.ADDTOCART_BEFORE_SALES_STARTDATE);
  }

  public isRefundVisible(statusId: number, substatusId: number): boolean {
    return !!((statusId === STATUS_REFUND && substatusId) || substatusId !== SUBSTATUS_SOLD_OUT) ||
      this.authService.checkPrivileges(
        [Privileges.REFUND_ALL, Privileges.REFUND_SALES_TERMINAL, Privileges.REFUND_WEB,
          Privileges.REFUND_PROMOTER, Privileges.REFUND_EXPIRED, Privileges.REFUND_EXCHANGE]);
  }

  // ticket purchase view is disabled if:
  // 1. cart contains ticket(s) of event in preparation status and that event is different from the current event
  // 2. cart contains ticket(s) of event(s) not in preparation status and the current event is in preparation status
  // 3. cart contains ticket(s) of event(s) that are self-service event and event is not self-service or opposite
  public isPurchaseViewDisabled(event: Event, cartDraftEvent: CartDraftEvent): boolean {
    return cartDraftEvent && ((cartDraftEvent.isDraft && event.eventId !== cartDraftEvent.id)
        || (!cartDraftEvent.isDraft && this.isDraft(event.status.id)) || (cartDraftEvent.boEvent !== event.boEvent));
  }

  public displayNavigateToEventView(event: Event, cartDraftEvent: CartDraftEvent): boolean {
    return !this.isPurchaseViewDisabled(event, cartDraftEvent) && this.hasAllowedSubstatus(event) &&
      this.hasAllowedStatus(event.status.id) && this.isRefundVisible(event.status.id, event.substatusId) && !this.isPresaleEnded(event.substatusId);
  }

  public displaySaleActions(event: Event): boolean {
    return this.hasAllowedSubstatus(event) && this.hasAllowedStatus(event.status.id) &&
      !this.isPresaleEnded(event.substatusId) && this.isSalesAllowed(event) &&
      (this.canBuy(event) || this.isTicketsUnavailable(event.substatusId) ||
        this.isPresaleWithCode(event) || (this.isSoldOut(event.substatusId) && !this.authService.hasRefundExchangePrivilege));
  }

  public displayRefundActions(event: Event): boolean {
    return this.isRefundAllowed(event) && this.isRefundVisible(event.status.id, event.substatusId) &&
      (this.isRefundable(event.status.id) || (this.isSoldOut(event.substatusId) && this.authService.hasRefundExchangePrivilege));
  }

  public isFadedOut(event: Event, cartDraftEvent: CartDraftEvent): boolean {
    return [SUBSTATUS_PRE_SALE_ENDED, SUBSTATUS_SOLD_OUT, SUBSTATUS_SALE_STARTS_ON].includes(event.substatusId) || (cartDraftEvent && cartDraftEvent.boEvent !== event.boEvent) || this.isFadeOutState(event.assignedStates);
  }

  private isFadeOutState(assignedStates: EventStateEnum[]): boolean {
    const fadeOutStates = [EventStateEnum.SoldOut, EventStateEnum.TicketsCurrentlyUnavailable];
    return assignedStates.filter(state => fadeOutStates.includes(state)).length !== 0;
  }

  public isSalesAllowed(event: Event) {
    return event.visibilityType.includes(EventVisibilityType.Sale);
  }

  public isRefundAllowed(event: Event) {
    return event.visibilityType.includes(EventVisibilityType.Refund);
  }
}
